import { useQuery } from '@tanstack/react-query';
import { TrendingUp, Users, MapPinned, Image, Smartphone, Sparkles, Mic } from 'lucide-react';
import { api } from '@/lib/api';
import { fmtN } from '@/lib/utils';
import { PageHeader } from '@/components/PageHeader';

type Stats = {
  users: { total: number; new24h: number; new7d: number; new30d: number; active24h: number };
  trips: { total: number; active: number };
  feed: { posts: number; comments: number };
  esim: { purchases: number };
  ai: { messages7d: number; estimatedCostUsd7d: number };
  voice: { messages24h: number };
};

export function Dashboard() {
  const { data, isLoading, error } = useQuery<Stats>({
    queryKey: ['/admin/api/stats'],
    queryFn: () => api.get<Stats>('/admin/api/stats'),
    refetchInterval: 60_000,
  });

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Live KPIs across the platform" />

      {error && (
        <div className="ad-card p-4 text-error mb-4">
          Failed to load stats: {(error as Error).message}
        </div>
      )}

      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-6">
        <Stat
          icon={<Users size={16} />}
          label="Total users"
          value={data?.users.total}
          delta={data?.users.new24h ? `+${data.users.new24h} in 24h` : undefined}
        />
        <Stat
          icon={<TrendingUp size={16} />}
          label="Active 24h"
          value={data?.users.active24h}
          tone="accent"
        />
        <Stat
          icon={<MapPinned size={16} />}
          label="Active trips"
          value={data?.trips.active}
          delta={`${fmtN(data?.trips.total)} total`}
        />
        <Stat
          icon={<Image size={16} />}
          label="Feed posts"
          value={data?.feed.posts}
          delta={`${fmtN(data?.feed.comments)} comments`}
        />
        <Stat
          icon={<Smartphone size={16} />}
          label="eSIM purchases"
          value={data?.esim.purchases}
        />
        <Stat
          icon={<Sparkles size={16} />}
          label="AI messages (7d)"
          value={data?.ai.messages7d}
          delta={data?.ai.estimatedCostUsd7d != null ? `≈ $${data.ai.estimatedCostUsd7d.toFixed(2)}` : undefined}
          tone="accent"
        />
        <Stat
          icon={<Mic size={16} />}
          label="Voice msgs (24h)"
          value={data?.voice.messages24h}
        />
        <Stat
          icon={<TrendingUp size={16} />}
          label="New users (30d)"
          value={data?.users.new30d}
          delta={`${fmtN(data?.users.new7d)} in 7d`}
        />
      </div>

      <div className="ad-card p-6">
        <div className="text-sm text-text2">
          {isLoading
            ? 'Loading…'
            : 'Live counts. Refreshes every 60s. Charts and cohort analytics ship in the next pass.'}
        </div>
      </div>
    </div>
  );
}

function Stat({
  icon,
  label,
  value,
  delta,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | null | undefined;
  delta?: string;
  tone?: 'accent';
}) {
  return (
    <div className="ad-stat">
      <div className="flex items-center gap-2 text-text3">
        {icon}
        <span className="ad-stat-label">{label}</span>
      </div>
      <div className={`ad-stat-value ${tone === 'accent' ? 'text-accent' : ''}`}>
        {fmtN(value)}
      </div>
      {delta && <div className="text-xs text-text2 mt-1">{delta}</div>}
    </div>
  );
}
